import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { Button, Callout, LoadingBlock } from '../design/components'
import { useT } from '../i18n'
import { useSession } from './SessionProvider'
import styles from './guards.module.css'

export type SignInRole = 'teacher' | 'student'

/** Router state left by a guard, so the sign-in page can explain itself and send people back. */
export interface SignInNotice {
  signIn: SignInRole
  /** Path (with search) to return to after signing in. */
  next: string
}

function isSignInNotice(value: unknown): value is SignInNotice {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Partial<SignInNotice>
  return (v.signIn === 'teacher' || v.signIn === 'student') && typeof v.next === 'string'
}

/** Read the notice a guard left in router state (null when the page was opened directly). */
export function useSignInNotice(): SignInNotice | null {
  const { state } = useLocation()
  return isSignInNotice(state) ? state : null
}

/** "Please sign in to see your classes." / "Please join your class first." */
export function SignInNoticeCallout({ notice }: { notice: SignInNotice }) {
  const t = useT()
  return (
    <Callout tone="info" icon="lock" live="status">
      {notice.signIn === 'teacher'
        ? t.common.session.teacherSignIn
        : t.common.session.studentSignIn}
    </Callout>
  )
}

/** Full-page message when /api/session can't be reached at all. */
export function SessionUnavailable({ onRetry }: { onRetry: () => void }) {
  const t = useT()
  return (
    <main className={styles.page} id="main">
      <Callout
        tone="warn"
        live="alert"
        title={t.common.session.unreachableTitle}
        action={
          <Button variant="primary" icon="refresh" onClick={onRetry}>
            {t.common.actions.tryAgain}
          </Button>
        }
      >
        {t.common.session.unreachableBody}
      </Callout>
    </main>
  )
}

function Guard({
  role,
  redirectTo,
  children,
}: {
  role: SignInRole
  redirectTo: string
  children?: ReactNode
}) {
  const t = useT()
  const session = useSession()
  const location = useLocation()
  if (session.status === 'loading') {
    return (
      <main className={styles.page} id="main">
        <LoadingBlock label={t.common.session.checking} />
      </main>
    )
  }
  if (session.status === 'error') return <SessionUnavailable onRetry={session.refetch} />
  const signedIn = role === 'teacher' ? session.teacher : session.student
  if (!signedIn) {
    const notice: SignInNotice = { signIn: role, next: `${location.pathname}${location.search}` }
    return <Navigate to={redirectTo} replace state={notice} />
  }
  return <>{children ?? <Outlet />}</>
}

/** Teacher-only routes. Without a teacher session → the landing page (Google or demo sign-in). */
export function RequireTeacher({ children }: { children?: ReactNode }) {
  return (
    <Guard role="teacher" redirectTo="/">
      {children}
    </Guard>
  )
}

/** Student-only routes. Without a student session → /join (door code → name → pictures). */
export function RequireStudent({ children }: { children?: ReactNode }) {
  return (
    <Guard role="student" redirectTo="/join">
      {children}
    </Guard>
  )
}
